/**
 * City events — events happening in the venue's CITY, authored by Feather and served by our own
 * REST API instead of Firestore. `city-events/CONTRACT.md` is the frozen seam: §3 is the document
 * decoded below, §4 the endpoints that carry it.
 *
 * ── The one rule this file exists to keep ──────────────────────────────────────────────────────
 * A city event is the SAME document as a venue event, in a different store, with no venue. So there
 * is no `CityEvent` domain model: this file is a second *decoder* into the existing {@link EventDoc}
 * (tracker I-2, I-3), and everything above it — cards, details, save-to-calendar — is the code that
 * is already there. iOS proved this out first (`CityEvent.swift`), and `EventDoc` gained exactly the
 * two fields that ruling added (`organizerName`, `organizerLogoUrl`) and nothing else.
 *
 * ── Why the decode looks so thin ───────────────────────────────────────────────────────────────
 * The backend emits the Firestore document's own key names, nesting and types, so `EventPrice`,
 * `EventLocation`, `EventMediaItem` and `EventTime` need no mapping at all — they are the same six /
 * seven keys either way. That is the contract's "no second dialect" rule paying for itself: if these
 * ever stop matching, the defect is the divergence, not the decoder (say so, do not adapt around it).
 *
 * ── Two decode facts that are contract, not preference ─────────────────────────────────────────
 * ⚠️ **camelCase, no key conversion.** Our endpoint speaks the Firestore document's camelCase, not
 *    the Menu API's snake_case — nothing here may lower-case `businessCountryISO`.
 * ⚠️ **Dates are ISO-8601 with a REAL offset that follows DST** (`+02:00` for Ohrid in August,
 *    `+01:00` for the same city in January), because the backend derives it per event from the
 *    city's timezone. They stay strings and are read through `toDate()` in `events/event-utils.ts`,
 *    which parses them as instants — exactly as it already does for a JSON round-tripped venue
 *    event. Never assume one fixed offset per city: that parses, and is wrong.
 */

import { CityEventsClientFields } from './client.model';
import { EventDoc, EventLocation, EventMediaItem, EventPrice } from './event.model';

/** Which half of the city's programme to ask for. `upcoming` is the API default. */
export type CityEventSegment = 'upcoming' | 'past';

/**
 * The city the events belong to, from `meta.city`.
 *
 * ⚠️ `null` does NOT mean "error". A venue with no city, a venue whose city is inactive and a venue
 * with nothing published all return one byte-identical empty `200` with `"city": null`. Whether the
 * City segment exists at all is decided BEFORE any request, from `city_events_enabled` + `city` on
 * the client payload — never from an empty list.
 */
export interface CityEventsCity {
  id?: number | null;
  name?: string | null;
  countryISO?: string | null;
}

/** `meta` on the list response — `CONTRACT.md` §4.1. */
export interface CityEventsMeta {
  city?: CityEventsCity | null;
  /**
   * Rows matching the CURRENT segment + featured filter, not the city's whole catalogue. Two
   * segments, two totals — which is what lets the featured strip and the list page independently.
   */
  total?: number | null;
  limit?: number | null;
  offset?: number | null;
  /**
   * ⚠️ **The only correct "fetch another page?" signal** (tracker **I-11**). Computed server-side as
   * `offset + count(data) < total`.
   *
   * It exists because the venue path's `count % pageSize === 0` guess is wrong in both directions:
   * with exactly 10/20/30 events the predicate never goes false and the list re-requests an empty
   * page every time the last card appears, and the moment a page yields fewer items than were
   * fetched the count stops being a multiple and paging halts early, silently hiding events. Do not
   * carry that pattern onto this path.
   */
  hasMore?: boolean | null;
}

/**
 * One city-event document, exactly as `CONTRACT.md` §3 defines it.
 *
 * Every field is optional and a present field may still be `null` — the same convention `EventDoc`
 * follows, and for the same reason: the backend writes explicit nulls rather than omitting keys.
 */
export interface CityEventDocument {
  id?: string | null;
  feedItemType?: string | null;
  active?: boolean | null;
  isFeatured?: boolean | null;

  /**
   * ⚠️ Always `null` on a city event, by design and by the backend's own guard (**D-6**): a city
   * event has no venue, and borrowing another venue's name or logo is the conflict of interest this
   * feature exists to avoid. `businessCountryISO` is the one venue-shaped key that IS filled — from
   * the city's country, not from any venue.
   */
  businessId?: string | null;
  businessName?: string | null;
  businessLogoUrl?: string | null;
  businessCountryISO?: string | null;

  /** Additive city-only keys — what replaces the venue. */
  cityEvent?: boolean | null;
  cityId?: number | null;
  cityName?: string | null;
  organizerName?: string | null;
  organizerLogoUrl?: string | null;
  /** What a diner can click to check us (R-4). Not rendered anywhere yet. */
  sourceUrl?: string | null;

  /** Default-language text. The backend mirrors it into the maps below, so it is the fallback. */
  title?: string | null;
  description?: string | null;
  /**
   * Per-language maps, e.g. `{"mk": "…", "en": "…"}`.
   *
   * ⚠️ **Key order is not stable** (tracker **I-14**) — MySQL normalises JSON object keys, and the
   * live endpoint has been observed serving `en, mk` where the fixture has `mk, en`. They are
   * dictionaries: read them BY KEY, never by position.
   */
  titleTranslations?: Record<string, string> | null;
  descriptionTranslations?: Record<string, string> | null;

  /** `startDate` / `endDate` are ISO-8601 strings with an offset; `isRepetitive` is a plain bool. */
  eventDate?: {
    startDate?: string | null;
    endDate?: string | null;
    isRepetitive?: boolean | null;
  } | null;
  /** ⚠️ `endTime` is `""` and not `null` when absent, matching what the venue writer emits. */
  eventTime?: {
    startTime?: string | null;
    endTime?: string | null;
  } | null;

  /** ⚠️ `price` is a number, never a string, and an integer stays an integer (`300`). */
  prices?: EventPrice[] | null;
  currency?: string | null;

  /**
   * The same six keys `EventMediaItem` carries, so it is passed straight through.
   * ⚠️ `orientation` / `itemWidth` / `itemHeight` may be `null` on older rows (tracker **I-29**) —
   * harmless here, since nothing on the web events screen reads any of the three.
   */
  mediaItems?: EventMediaItem[] | null;
  /**
   * ⚠️ Can be `[]` — a city event may publish with no location at all (**I-27**, ruled). Both mobile
   * apps had to fix an index-out-of-bounds here. `primaryLocation()` already indexes optionally, so
   * an empty array is a normal response on the web path rather than a crash.
   */
  locations?: EventLocation[] | null;

  /** ⚠️ `""` and not `null` when unset, again matching the venue writer. */
  phoneNumber?: string | null;
  eventWebUrl?: string | null;
}

/** `GET /api/v3/shops/{shopId}/city-events` — `CONTRACT.md` §4.1. */
export interface CityEventsListResponse {
  data?: CityEventDocument[] | null;
  meta?: CityEventsMeta | null;
}

/** `GET /api/v3/city-events/{uuid}` — `CONTRACT.md` §4.2, the deeplink route. */
export interface CityEventDetailResponse {
  data?: CityEventDocument | null;
}

/**
 * One page of city events, resolved for the caller.
 *
 * Deliberately NOT shaped like `EventsPage`: Firestore pages by opaque cursor, this endpoint pages
 * by `offset` + a server-computed `hasMore`, and flattening one onto the other would hide exactly
 * the difference **I-11** is about.
 */
export interface CityEventsPage {
  events: EventDoc[];
  /** Straight off `meta.hasMore`. The ONLY thing a caller may use to decide "fetch another page". */
  hasMore: boolean;
  /** Rows matching this segment + featured filter. For display / diagnostics, never for paging. */
  total: number;
  city: CityEventsCity | null;
}

/**
 * Current language → `en` → the scalar the backend mirrors for that purpose.
 *
 * The exact chain `LanguageService.localizeMap` implements, as a free function so the mapper below
 * stays pure and testable without the service — the same reason `resolveReelText` exists in
 * `reel.model.ts`. Blank values fall through, so a half-translated event shows English rather than
 * an empty title.
 */
export function localizedCityText(
  map: Record<string, string> | null | undefined,
  lang: string,
  fallback: string
): string {
  if (map && typeof map === 'object') {
    if (map[lang]) return map[lang];
    if (map['en']) return map['en'];
  }
  return fallback;
}

/**
 * The second decoder into the same domain model — the whole of the web data work (**I-3**).
 *
 * What is NOT carried over, and why:
 *  - `businessId` / `businessName` / `businessLogoUrl` — `null` on the wire and left null here.
 *    This is the ownerless state the model already anticipates; a card asks the `EventDoc` and gets
 *    the honest answer. **Never** substitute the venue the diner is sitting in (**D-6**).
 *  - `bookAction`, `relatedVoucherIDs`, `relatedFeedItems`, `category`, `type`, `ageSuitability`,
 *    `outdoorEvent`, `skillIDs` — not in the city-event document at all. A city event has no
 *    voucher and no booking of ours to offer; `eventWebUrl` is the organiser's own link.
 *  - `cityEvent` / `cityId` / `cityName` / `sourceUrl` — decoded but not mapped: nothing renders
 *    them, and §3 marks them explicitly safe to ignore. Adding a field to `EventDoc` for one of
 *    them is what the "no new domain model" rule forbids.
 *
 * `lang` is resolved here rather than at render time, mirroring iOS: the venue path's `title` is a
 * plain string, so every consumer already treats it as resolved text. A diner who switches language
 * mid-screen therefore keeps the city titles they loaded with until the list is fetched again — the
 * mapper is pure, so re-mapping retained documents is available if that ever needs to change.
 */
export function toEventDoc(doc: CityEventDocument, lang: string): EventDoc {
  return {
    id: doc.id ?? null,

    businessId: doc.businessId ?? null,
    businessName: doc.businessName ?? null,
    businessLogoUrl: doc.businessLogoUrl ?? null,
    businessCountryISO: doc.businessCountryISO ?? null,

    // The two fields City Events adds to the shared model, and the only two (tracker I-21): a city
    // event has no venue to name, so without them its details screen has an empty identity block.
    organizerName: doc.organizerName ?? null,
    organizerLogoUrl: doc.organizerLogoUrl ?? null,

    title: localizedCityText(doc.titleTranslations, lang, doc.title ?? ''),
    description: localizedCityText(doc.descriptionTranslations, lang, doc.description ?? ''),

    prices: doc.prices ?? null,
    currency: doc.currency ?? null,

    mediaItems: doc.mediaItems ?? null,
    locations: doc.locations ?? null,

    active: doc.active ?? null,
    isFeatured: doc.isFeatured ?? null,

    eventDate: doc.eventDate
      ? {
          startDate: doc.eventDate.startDate ?? null,
          endDate: doc.eventDate.endDate ?? null,
          // The document spells this `isRepetitive` (the app models' name); Firestore spells it
          // `repetitive`, which is what `EventDate` declares. Mapped onto the field the model has,
          // so the one place the two dialects disagree is here rather than in every consumer.
          repetitive: doc.eventDate.isRepetitive ?? null,
        }
      : null,
    eventTime: doc.eventTime
      ? { startTime: doc.eventTime.startTime ?? null, endTime: doc.eventTime.endTime ?? null }
      : null,

    phoneNumber: doc.phoneNumber ?? null,
    eventWebUrl: doc.eventWebUrl ?? null,

    feedItemType: doc.feedItemType ?? null,
  };
}

/**
 * The list envelope, resolved into a page.
 *
 * ⚠️ A missing or malformed `meta` defaults `hasMore` to **false** — stop, rather than loop. The
 * failure mode of guessing wrong in the other direction is an endless request loop against an
 * endpoint that keeps answering `200`, which is worse than one page of missing events.
 */
export function toCityEventsPage(
  response: CityEventsListResponse | null | undefined,
  lang: string
): CityEventsPage {
  const documents = response?.data ?? [];
  const meta = response?.meta ?? null;

  return {
    events: documents.map(doc => toEventDoc(doc, lang)),
    hasMore: meta?.hasMore === true,
    total: typeof meta?.total === 'number' ? meta.total : 0,
    city: meta?.city ?? null,
  };
}

// ---------------------------------------------------------------------------
// section state
// ---------------------------------------------------------------------------

/**
 * One of the City segment's two sections — the featured strip and the list below it — as a value.
 *
 * The two are independent queries with independent `hasMore`s and independent offsets (`meta.total`
 * counts the current segment + featured filter, so they page separately by design). Modelled as
 * plain data folded by a pure function rather than as fields on the component, so **I-11 is testable
 * with no component, no TestBed and no HTTP** — the shape Android landed on (`CityEventsSection`)
 * and the one thing about this feature that is easiest to get quietly wrong.
 */
export interface CityEventsSection {
  events: EventDoc[];
  /** ⚠️ Straight off `meta.hasMore`. The ONLY signal that may decide "fetch another page". */
  hasMore: boolean;
  /**
   * Rows the SERVER has already handed out for this section — the next request's `offset`.
   *
   * Deliberately not `events.length`: the fold drops duplicates (offset paging can repeat a row when
   * the superadmin publishes while a diner is reading — `CONTRACT.md` §4.1 accepts that), and paging
   * on the deduped count would shift the window back and re-request rows that were already dropped.
   */
  offset: number;
  /** The first page is in flight. */
  loading: boolean;
  /** A later page is in flight; the section already has events on screen. */
  loadingMore: boolean;
  /**
   * The last request for this section failed.
   *
   * ⚠️ Load-bearing beyond the error state: an error is NOT emptiness, and the segment must not be
   * hidden because a request died — see {@link cityEventsSegmentVisible}.
   */
  failed: boolean;
}

export function emptyCityEventsSection(): CityEventsSection {
  return { events: [], hasMore: false, offset: 0, loading: false, loadingMore: false, failed: false };
}

/**
 * Fold one page into a section. Pure — no component state, no service.
 *
 * `page === null` is a failed request (the service swallows failures to null and reports them): the
 * section stops paging rather than re-requesting the same offset on every scroll tick, and keeps
 * whatever it already had on screen, because half a list beats a blanked one.
 *
 * ⚠️ **`hasMore` comes from the page and nothing else** (**I-11**). Never `events.length % limit`:
 * on an exact multiple that predicate stays true forever and the list re-requests an empty page
 * every time the last card appears, and the moment a page yields fewer rows than were fetched it
 * halts early and silently hides everything after it.
 */
export function appendCityEventsPage(
  section: CityEventsSection,
  page: CityEventsPage | null
): CityEventsSection {
  if (!page) {
    return { ...section, loading: false, loadingMore: false, failed: true, hasMore: false };
  }

  const seen = new Set(section.events.map(e => e.id).filter(Boolean));
  const fresh = page.events.filter(e => !e.id || !seen.has(e.id));

  return {
    events: section.events.concat(fresh),
    hasMore: page.hasMore,
    // Server rows, not kept rows — see the field's own note.
    offset: section.offset + page.events.length,
    loading: false,
    loadingMore: false,
    failed: false,
  };
}

// ---------------------------------------------------------------------------
// whether the segment exists at all
// ---------------------------------------------------------------------------

/**
 * Half one of the rule (GZ, 2026-08-12): **the venue has a city.**
 *
 * Decided from the client payload **before any request** — never from an empty list, which cannot
 * tell "no city" from "nothing published" from a failure. Both halves are required: the key is not a
 * feature flag any more (see `CityEventsClientFields`), and a `true` with no city block is a venue
 * whose city was unlinked, which must show nothing.
 */
export function cityEventsAllowed(clientData: CityEventsClientFields | null | undefined): boolean {
  return clientData?.city_events_enabled === true && !!clientData?.city;
}

/**
 * The rule in full: show the City segment when the venue has a city **AND that city has events**.
 *
 * ⚠️ The second half is the delicate one:
 *  - hidden while the FIRST load is in flight. Showing it there and hiding it when the load comes
 *    back empty is strictly worse than appearing a beat late: the control vanishes from under a
 *    diner who is already looking at it, and in that window it is a real button — a tap on City is
 *    then undone by `loadCityInitial` after `city_events_segment_switch` has already been logged,
 *    which counts a choice that led nowhere. It appears while the skeletons are still up, so there
 *    is nothing on screen for it to interrupt;
 *  - shown after a failure — a dead request is not an empty city, and a diner who hits one would
 *    otherwise lose the segment for the rest of the visit with nothing to retry;
 *  - hidden after a load that FINISHED and found nothing in either section.
 *
 * Note what this forces, and it is the reason it is written here rather than left to the component:
 * **the city load has to be eager**, alongside the venue one. A lazy load can only discover the city
 * is empty *after* the diner has already tapped City — and hiding the control they just pressed is
 * the one outcome worse than either flicker. iOS and Android both load lazily and neither has
 * shipped this half yet; if they adopt it, that is the piece to change first.
 */
export function cityEventsSegmentVisible(
  allowed: boolean,
  loadFinished: boolean,
  featured: CityEventsSection,
  all: CityEventsSection
): boolean {
  if (!allowed) return false;
  if (!loadFinished) return false;
  if (featured.failed || all.failed) return true;
  return featured.events.length > 0 || all.events.length > 0;
}
